import { User } from "@/stores/AuthStore";
import Cookies from "js-cookie";
import api, { getCsrfCookie } from "./api";

interface LoginCredentials {
  email: string;
  password: string;
}

interface LoginResponse {
  user: User;
  abilities: string[];
  access_token: string;
  message: string;
}

interface LoginErrorResponse {
  message: string;
  code?: string;
}

export class LoginError extends Error {
  constructor(message: string, public readonly code?: string) {
    super(message);
    this.name = 'LoginError';
  }
}

class AuthService {
  async login(credentials: LoginCredentials): Promise<User> {
    // Não envia ao endpoint de login um perfil salvo de uma sessão anterior.
    Cookies.remove("active_role");
    await getCsrfCookie();

    try {
      const response = await api.post<LoginResponse>("/auth/login", credentials);

      Cookies.set("auth_token", response.data.access_token, {
        expires: 7,
        secure: process.env.NODE_ENV === "production",
        sameSite: "lax",
      });
      return response.data.user;
    } catch (error: any) {
      if (error.response?.status === 403 && error.response?.data?.code === 'ALUNO_DESLIGADO') {
        const errorData: LoginErrorResponse = error.response.data;
        throw new Error(errorData.message || 'Seu vínculo como aluno foi desligado. Entre em contato com a coordenação.');
      }

      const code = error.response?.data?.code;

      if (error.response?.status === 403 && (code === 'ACESSO_PENDENTE' || code === 'CONTA_INATIVA' || code === 'VINCULO_NAO_ENCONTRADO')) {
        throw new LoginError(error.response.data.message, code);
      }

      if (error.response?.status === 401) {
        throw new Error('Email ou senha incorretos. Por favor, verifique suas credenciais.');
      }

      throw new Error(error.response?.data?.message || 'Erro ao realizar login. Tente novamente.');
    }
  }

  async logout(): Promise<void> {
    await api.post("/auth/logout");
  }

  async getUser(): Promise<User> {
    const response = await api.get<{ data: User }>("/auth/me");

    const userData = response.data.data || response.data;
    return userData as User;
  }
}

export const authService = new AuthService();
